import { ArrowsClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowsClockwise";
import { DotsThreeIcon } from "@phosphor-icons/react/dist/csr/DotsThree";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";

interface RosterMoreActionsMenuProps {
  onImportValues: () => void;
  // Empty until the published script declares a roster contract, and there is
  // nothing to import values into until it does.
  canImportValues: boolean;
  // Only users authorized to update the complete roster receive this callback.
  // Other users do not see an action that the API would reject.
  onSyncTechnicians?: () => void;
  isSyncing?: boolean;
  busy?: boolean;
}

// The occasional roster actions. Adding a person is the thing an owner comes
// here to do and keeps its own button; importing and syncing are periodic, so
// they sit behind the tertiary menu rather than competing with it.
export const RosterMoreActionsMenu = ({
  onImportValues,
  canImportValues,
  onSyncTechnicians,
  isSyncing = false,
  busy = false,
}: RosterMoreActionsMenuProps) => (
  <>
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            size="icon"
            variant="secondary"
            aria-label="More roster actions"
            loading={isSyncing}
            loadingLabel="More roster actions, syncing technicians"
            disabled={busy || isSyncing}
          />
        }
      >
        <DotsThreeIcon className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-64">
        <DropdownMenuItem
          disabled={!canImportValues || busy}
          onClick={onImportValues}
        >
          <UploadSimpleIcon className="h-4 w-4" />
          <Stack align="start" gap={0}>
            <Text>Import values</Text>
            {!canImportValues && (
              <Text variant="caption" tone="muted">
                Available once the script sets the roster columns
              </Text>
            )}
          </Stack>
        </DropdownMenuItem>
        {onSyncTechnicians && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled={busy} onClick={onSyncTechnicians}>
              {!isSyncing && <ArrowsClockwiseIcon className="h-4 w-4" />}
              {isSyncing
                ? "Syncing technicians…"
                : "Sync ServiceTitan technicians"}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    <span role="status" className="sr-only">
      {isSyncing ? "Sync ServiceTitan technicians in progress" : ""}
    </span>
  </>
);
